import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, Hash, Clock, Check, AlertCircle } from "lucide-react";
import { IOrder } from "../../models/Order.model";

interface PaymentDetails {
  utrNumber: {
    [key: string]: { utrNumber: string; amount: number; isCompleted: boolean };
  };
  paymentScreenshot: any[];
}

type UTRModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedOrder: IOrder | null;
  onConfirm: (type: "utr" | "screenshot", key: string) => Promise<void>;
};

const UTRModal: React.FC<UTRModalProps> = ({
  isOpen,
  onClose,
  selectedOrder,
  onConfirm,
}) => {
  const [selectedUtr, setSelectedUtr] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);

  const handleUtrCheckboxChange = (utr: string) => {
    // If clicking on already selected UTR, unselect it
    if (selectedUtr === utr) {
      setSelectedUtr(null);
    } else {
      // Otherwise, select this UTR (automatically unselects previous)
      setSelectedUtr(utr);
    }
  };

  const handleConfirmUTR = async (key: string) => {
    setIsProcessing(key);
    try {
      await onConfirm("utr", key);
      // Reset selection after confirmation
      setSelectedUtr(null);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsProcessing(null);
    }
  };

  const paymentDetails =
    selectedOrder?.paymentDetails as unknown as PaymentDetails;
  const pendingUTRs = Object.entries(paymentDetails?.utrNumber || {}).filter(
    ([_, data]: any) => data.isCompleted === false
  );
  const completedUTRs = Object.entries(paymentDetails?.utrNumber || {}).filter(
    ([_, data]: any) => data.isCompleted === true
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden border border-slate-200"
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            transition={{ type: "spring", duration: 0.4, bounce: 0.1 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-slate-100 rounded-md flex items-center justify-center">
                  <Hash className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    UTR Verification
                  </h2>
                  <p className="text-sm text-slate-500">
                    Review and approve payment transactions (Select one at a
                    time)
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 bg-white rounded-md flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors border border-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex h-[calc(85vh-80px)]">
              {/* Pending UTRs - Left Panel */}
              <div className="flex-1 border-r border-slate-200">
                <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <h3 className="font-medium text-slate-900">
                        Pending Verification
                      </h3>
                    </div>
                    <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-medium">
                      {pendingUTRs.length}
                    </span>
                  </div>
                </div>

                <div className="overflow-y-auto h-full">
                  {pendingUTRs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center px-6">
                      <AlertCircle className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500 font-medium">
                        No pending UTRs
                      </p>
                      <p className="text-sm text-slate-400">
                        All transactions have been processed
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 space-y-3">
                      {pendingUTRs.map(([utrNumber, data], idx) => (
                        <motion.div
                          key={idx}
                          className={`bg-white border rounded-md p-4 transition-colors ${
                            selectedUtr === utrNumber
                              ? "border-blue-300 bg-blue-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.05 }}
                        >
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={selectedUtr === utrNumber}
                              onChange={() =>
                                handleUtrCheckboxChange(utrNumber)
                              }
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <Hash className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                <span className="font-mono text-sm text-slate-900 truncate">
                                  {utrNumber}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                              ₹{data.amount.toLocaleString()}
                            </div>
                          </div>

                          {selectedUtr === utrNumber && (
                            <motion.div
                              className="mt-3 pt-3 border-t border-slate-100 flex justify-end"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              transition={{ duration: 0.2 }}
                            >
                              <button
                                onClick={() => handleConfirmUTR(utrNumber)}
                                disabled={isProcessing === utrNumber}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                              >
                                {isProcessing === utrNumber ? (
                                  <>
                                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    <span>Processing</span>
                                  </>
                                ) : (
                                  <>
                                    <Check className="w-3 h-3" />
                                    <span>Approve</span>
                                  </>
                                )}
                              </button>
                            </motion.div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Completed UTRs - Right Panel */}
              <div className="w-80 bg-slate-50">
                <div className="px-6 py-4 bg-slate-100 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <h3 className="font-medium text-slate-900">Completed</h3>
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                      {completedUTRs.length}
                    </span>
                  </div>
                </div>

                <div className="overflow-y-auto h-full">
                  {completedUTRs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center px-6">
                      <CheckCircle className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500 font-medium">
                        No completed UTRs
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 space-y-3">
                      {completedUTRs.map(([utrNumber, data], idx) => (
                        <motion.div
                          key={idx}
                          className="bg-white border border-green-200 rounded-md p-3"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.05 }}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-2 h-2 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-1 mb-1">
                                <Hash className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                <span className="font-mono text-xs text-slate-700 truncate">
                                  {utrNumber}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-slate-700">
                                  ₹{data.amount}
                                </span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UTRModal;
